import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FiX, FiPlus, FiTrash2, FiEdit2 } from 'react-icons/fi';
import EditFOCustomerModal from './EditFOCustomerModal';
import CreateFOCustomerForm from './CreateFOCustomerForm';
import EditCategoryModal from './EditCategoryModal';

const EditFOOrderModal = ({ order, onSuccess, onCancel }) => {
    const [formData, setFormData] = useState({
        purchaseCode: '',
        webOrderID: '',
        trackingNumber: '',
        customer: '',
        orderEmail: '',
        orderAddress: '',
        orderPhone: '',
        orderWhatsapp: '',
        orderTelegram: { username: '', tgNumber: '' },
        items: [{
            orderingcode: '',
            category: '',
            subcategory: '',
            brand: '',
            orderDate: '',
            sellingPrice: '',
            costRMB: '',
            supplier: '',
            doReceiveDate: '',
            warehouseRcvDate: '',
            costInSGD: '',
            itemProfitSGD: '',
            remarks: ''
        }],
        deliveryFees: '',
        discount: '',
        shippedOutDate: '',
        customerReceiveDate: '',
        shippingBillDate: '',
        logistic: '',
        rate: '',
        shipCostSGD: '',
        shipCostRMB: '',
        totalProfit: '',
        margin: '',
        paymentMethod: 'SGD',
        remarks: '',
        creditNotes: []
    });
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [customers, setCustomers] = useState([]);
    const [customerSearchTerm, setCustomerSearchTerm] = useState('');
    const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
    const [selectedCustomerDetails, setSelectedCustomerDetails] = useState(null);
    const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);
    const [showCreateCustomerModal, setShowCreateCustomerModal] = useState(false);
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [categorySubcategories, setCategorySubcategories] = useState({}); // Stores subcategories for each item when category is selected
    const [filteredCategories, setFilteredCategories] = useState({}); // Stores filtered category IDs for each item when subcategory is selected first
    
    // Search states for each item field
    const [itemSearchTerms, setItemSearchTerms] = useState({}); // { itemIndex: { brand: '', category: '', subcategory: '', supplier: '' } }
    const [itemDropdowns, setItemDropdowns] = useState({}); // { itemIndex: { brand: false, category: false, subcategory: false, supplier: false } }

    // EditCategoryModal state (Add category / Add subcategory open this modal)
    const [showEditCategoryForSubcategory, setShowEditCategoryForSubcategory] = useState(false);
    const [editCategoryItemIndex, setEditCategoryItemIndex] = useState(null);
    const [editCategoryInitial, setEditCategoryInitial] = useState(null);
    const [editCategoryPrefillCategory, setEditCategoryPrefillCategory] = useState('');
    const [editCategoryPrefillSub, setEditCategoryPrefillSub] = useState('');

    // Track which order we initialized from so we only reset form when order changes (not when suppliers/brands/categories change)
    const orderIdInitializedRef = useRef(null);

    // Fetch customers
    useEffect(() => {
        const fetchCustomers = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/focustomers?page=1&limit=100`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'ngrok-skip-browser-warning': 'true'
                        }
                    }
                );
                if (response.data.success) {
                    setCustomers(response.data.data);
                }
            } catch (err) {
                console.error('Failed to fetch customers:', err);
            }
        };
        fetchCustomers();
    }, []);

    // Fetch categories (exposed so EditCategoryModal onSuccess can refetch)
    const fetchCategories = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/foproducts/categories?page=1&limit=100`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            if (response.data.success) {
                setCategories(response.data.data);
            }
        } catch (err) {
            console.error('Failed to fetch categories:', err);
        }
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    // Fetch brands
    useEffect(() => {
        const fetchBrands = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/foproducts/brands?page=1&limit=100`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'ngrok-skip-browser-warning': 'true'
                        }
                    }
                );
                if (response.data.success) {
                    setBrands(response.data.data);
                }
            } catch (err) {
                console.error('Failed to fetch brands:', err);
            }
        };
        fetchBrands();
    }, []);

    // Fetch suppliers
    useEffect(() => {
        const fetchSuppliers = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/foproducts/suppliers?page=1&limit=100`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'ngrok-skip-browser-warning': 'true'
                        }
                    }
                );
                if (response.data.success) {
                    setSuppliers(response.data.data);
                }
            } catch (err) {
                console.error('Failed to fetch suppliers:', err);
            }
        };
        fetchSuppliers();
    }, []);

    // Populate form data from order prop (only when order changes; not when suppliers/brands/categories change)
    useEffect(() => {
        if (!order) return;
        const orderId = order._id;
        const orderChanged = orderIdInitializedRef.current !== orderId;
        if (!orderChanged) return; // Same order: don't reset form (e.g. user added a supplier/brand/category)
        orderIdInitializedRef.current = orderId;

        // Format dates for input fields (YYYY-MM-DD)
        const formatDateForInput = (dateString) => {
                if (!dateString) return '';
                const date = new Date(dateString);
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };

            // Format shippingBillDate
            let shippingBillDateFormatted = '';
            if (order.shippingBillDate) {
                shippingBillDateFormatted = formatDateForInput(order.shippingBillDate);
            }

            // Initialize form data
            const custId = order.customer ? (typeof order.customer === 'object' ? order.customer._id : order.customer) : '';
            const initialFormData = {
                purchaseCode: order.purchaseCode || '',
                webOrderID: order.webOrderID || '',
                trackingNumber: order.trackingNumber || '',
                customer: custId,
                orderEmail: order.orderEmail || '',
                orderAddress: order.orderAddress || '',
                orderPhone: order.orderPhone || '',
                orderWhatsapp: order.orderWhatsapp || '',
                orderTelegram: order.orderTelegram ? { username: order.orderTelegram.username || '', tgNumber: order.orderTelegram.tgNumber || '' } : { username: '', tgNumber: '' },
                items: order.items && order.items.length > 0 ? order.items.map(item => ({
                    orderingcode: item.orderingcode || '',
                    category: item.category ? (typeof item.category === 'object' ? item.category._id : item.category) : '',
                    subcategory: item.subcategory || '',
                    brand: item.brand ? (typeof item.brand === 'object' ? item.brand._id : item.brand) : '',
                    orderDate: formatDateForInput(item.orderDate),
                    sellingPrice: item.sellingPrice || '',
                    costRMB: item.costRMB || '',
                    supplier: item.supplier ? (typeof item.supplier === 'object' ? item.supplier._id : item.supplier) : '',
                    doReceiveDate: formatDateForInput(item.doReceiveDate),
                    warehouseRcvDate: formatDateForInput(item.warehouseRcvDate),
                    costInSGD: item.costInSGD || '',
                    itemProfitSGD: item.itemProfitSGD || '',
                    remarks: item.remarks || ''
                })) : [{
                    orderingcode: '',
                    category: '',
                    subcategory: '',
                    brand: '',
                    orderDate: '',
                    sellingPrice: '',
                    costRMB: '',
                    supplier: '',
                    doReceiveDate: '',
                    warehouseRcvDate: '',
                    costInSGD: '',
                    itemProfitSGD: '',
                    remarks: ''
                }],
                deliveryFees: order.deliveryFees || '',
                discount: order.discount || '',
                shippedOutDate: formatDateForInput(order.shippedOutDate),
                customerReceiveDate: formatDateForInput(order.customerReceiveDate),
                shippingBillDate: shippingBillDateFormatted,
                logistic: order.logistic || '',
                rate: order.rate || '',
                shipCostSGD: order.shipCostSGD || '',
                shipCostRMB: order.shipCostRMB || '',
                totalProfit: order.totalProfit || '',
                margin: order.margin || '',
                paymentMethod: order.paymentMethod || 'SGD',
                remarks: order.remarks || '',
                creditNotes: (order.creditNotes && Array.isArray(order.creditNotes))
                    ? order.creditNotes.map(cn => ({ type: cn.type, amountSGD: cn.amountSGD, remarks: cn.remarks || '' }))
                    : []
            };

            // Set form from order first (including order contact snapshot)
            setFormData(initialFormData);

            // Set customer search term and fetch full customer details
            if (order.customer) {
                const customerId = typeof order.customer === 'object' ? order.customer._id : order.customer;
                const customer = typeof order.customer === 'object' ? order.customer : customers.find(c => c._id === customerId);
                if (customer) {
                    setCustomerSearchTerm(`${customer.customerID} - ${customer.Name}`);
                    // Fetch full customer details
                    const fetchCustomerDetails = async () => {
                        try {
                            const token = localStorage.getItem('token');
                            const response = await axios.get(
                                `${process.env.REACT_APP_API_URL}/api/focustomers/${customerId}`,
                                {
                                    headers: {
                                        'Authorization': `Bearer ${token}`,
                                        'ngrok-skip-browser-warning': 'true'
                                    }
                                }
                            );
                            if (response.data.success) {
                                const c = response.data.data;
                                setSelectedCustomerDetails(c);
                                // If order had no contact snapshot, fill from customer's first of each
                                const o = order;
                                const hasOrderEmail = o.orderEmail != null && o.orderEmail !== '';
                                const hasOrderAddr = o.orderAddress != null && o.orderAddress !== '';
                                const hasOrderPhone = o.orderPhone != null && o.orderPhone !== '';
                                const hasOrderWa = o.orderWhatsapp != null && o.orderWhatsapp !== '';
                                const hasOrderTg = o.orderTelegram && ((o.orderTelegram.username || '') + (o.orderTelegram.tgNumber || ''));
                                if (!hasOrderEmail || !hasOrderAddr || !hasOrderPhone || !hasOrderWa || !hasOrderTg) {
                                    const addrArr = c.Address || [];
                                    setFormData(prev => ({
                                        ...prev,
                                        orderEmail: hasOrderEmail ? (o.orderEmail || '') : ((c.email && c.email[0]) || ''),
                                        orderAddress: hasOrderAddr ? (o.orderAddress || '') : (addrArr.length > 0 ? addrArr[addrArr.length - 1] : ''),
                                        orderPhone: hasOrderPhone ? (o.orderPhone || '') : ((c.Phone && c.Phone[0]) || ''),
                                        orderWhatsapp: hasOrderWa ? (o.orderWhatsapp || '') : ((c.Whatsapp && c.Whatsapp[0]) || ''),
                                        orderTelegram: hasOrderTg && o.orderTelegram ? { username: o.orderTelegram.username || '', tgNumber: o.orderTelegram.tgNumber || '' } : ((c.Telegram && c.Telegram[0]) ? { username: c.Telegram[0].username || '', tgNumber: c.Telegram[0].tgNumber || '' } : { username: '', tgNumber: '' })
                                    }));
                                }
                            }
                        } catch (err) {
                            console.error('Failed to fetch customer details:', err);
                            setSelectedCustomerDetails(customer);
                        }
                    };
                    fetchCustomerDetails();
                }
            }

            // Initialize item search terms from existing data
            const initialSearchTerms = {};
            if (order.items && order.items.length > 0) {
                order.items.forEach((item, index) => {
                    initialSearchTerms[index] = {
                        brand: item.brand && typeof item.brand === 'object' ? item.brand.Brand : (item.brand ? (brands.find(b => b._id === item.brand)?.Brand || '') : ''),
                        category: item.category && typeof item.category === 'object' ? item.category.category : (item.category ? (categories.find(c => c._id === item.category)?.category || '') : ''),
                        subcategory: item.subcategory || '',
                        supplier: item.supplier && typeof item.supplier === 'object' ? item.supplier.supplierCode : (item.supplier ? (suppliers.find(s => s._id === item.supplier)?.supplierCode || '') : '')
                    };
                });
            }
            setItemSearchTerms(initialSearchTerms);
    }, [order, customers, brands, categories, suppliers]);

    // Get subcategories for a category
    const getSubcategoriesForCategory = (categoryId) => {
        if (!categoryId) return [];
        const category = categories.find(c => c._id === categoryId);
        if (category && category.subcategory && Array.isArray(category.subcategory)) {
            return category.subcategory;
        }
        return [];
    };

    // Get categories that contain a specific subcategory
    const getCategoriesForSubcategory = (subcategoryName) => {
        if (!subcategoryName || !subcategoryName.trim()) return [];
        const trimmedSubcategory = subcategoryName.trim().toLowerCase();
        return categories.filter(cat => 
            cat.subcategory && 
            Array.isArray(cat.subcategory) && 
            cat.subcategory.some(sub => sub.toLowerCase() === trimmedSubcategory)
        );
    };

    // Get all unique subcategories across all categories
    const getAllSubcategories = () => {
        const allSubcategories = new Set();
        categories.forEach(cat => {
            if (cat.subcategory && Array.isArray(cat.subcategory)) {
                cat.subcategory.forEach(sub => {
                    if (sub && sub.trim()) {
                        allSubcategories.add(sub);
                    }
                });
            }
        });
        return Array.from(allSubcategories).sort();
    };

    // Filter customers based on search
    const filteredCustomers = customers.filter(customer => {
        if (!customerSearchTerm) return true;
        const searchLower = customerSearchTerm.toLowerCase();
        return (
            (customer.customerID && customer.customerID.toLowerCase().includes(searchLower)) ||
            (customer.Name && customer.Name.toLowerCase().includes(searchLower))
        );
    });

    const handleCustomerSelect = async (customerId) => {
        setFormData(prev => ({ ...prev, customer: customerId }));
        const customer = customers.find(c => c._id === customerId);
        if (customer) {
            setCustomerSearchTerm(`${customer.customerID} - ${customer.Name}`);
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/focustomers/${customerId}`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'ngrok-skip-browser-warning': 'true'
                        }
                    }
                );
                if (response.data.success) {
                    const c = response.data.data;
                    setSelectedCustomerDetails(c);
                    const addrArr = c.Address || [];
                    setFormData(prev => ({
                        ...prev,
                        customer: customerId,
                        orderEmail: (c.email && c.email[0]) || '',
                        orderAddress: addrArr.length > 0 ? addrArr[addrArr.length - 1] : '',
                        orderPhone: (c.Phone && c.Phone[0]) || '',
                        orderWhatsapp: (c.Whatsapp && c.Whatsapp[0]) || '',
                        orderTelegram: (c.Telegram && c.Telegram[0]) ? { username: c.Telegram[0].username || '', tgNumber: c.Telegram[0].tgNumber || '' } : { username: '', tgNumber: '' }
                    }));
                }
            } catch (err) {
                console.error('Failed to fetch customer details:', err);
                setSelectedCustomerDetails(customer);
                setFormData(prev => ({
                    ...prev,
                    customer: customerId,
                    orderEmail: '',
                    orderAddress: '',
                    orderPhone: '',
                    orderWhatsapp: '',
                    orderTelegram: { username: '', tgNumber: '' }
                }));
            }
        }
        setShowCustomerDropdown(false);
    };

    const addItem = () => {
        const newIndex = formData.items.length;
        setFormData({
            ...formData,
            items: [...formData.items, {
                orderingcode: '',
                category: '',
                subcategory: '',
                brand: '',
                orderDate: '',
                sellingPrice: '',
                costRMB: '',
                supplier: '',
                doReceiveDate: '',
                warehouseRcvDate: '',
                costInSGD: '',
                itemProfitSGD: '',
                remarks: ''
            }]
        });
        // Initialize search terms for new item
        setItemSearchTerms({
            ...itemSearchTerms,
            [newIndex]: { brand: '', category: '', subcategory: '', supplier: '' }
        });
        setItemDropdowns({
            ...itemDropdowns,
            [newIndex]: { brand: false, category: false, subcategory: false, supplier: false }
        });
    };

    const removeItem = (index) => {
        const newItems = formData.items.filter((_, i) => i !== index);
        setFormData({
            ...formData,
            items: newItems.length > 0 ? newItems : [{
                orderingcode: '',
                category: '',
                subcategory: '',
                brand: '',
                orderDate: '',
                sellingPrice: '',
                costRMB: '',
                supplier: '',
                doReceiveDate: '',
                warehouseRcvDate: '',
                costInSGD: '',
                itemProfitSGD: '',
                remarks: ''
            }]
        });
        // Clean up search terms for removed item
        const newSearchTerms = { ...itemSearchTerms };
        const newDropdowns = { ...itemDropdowns };
        delete newSearchTerms[index];
        delete newDropdowns[index];
        // Reindex remaining items
        const reindexedSearchTerms = {};
        const reindexedDropdowns = {};
        Object.keys(newSearchTerms).forEach(key => {
            const oldIndex = parseInt(key);
            if (oldIndex > index) {
                reindexedSearchTerms[oldIndex - 1] = newSearchTerms[key];
                reindexedDropdowns[oldIndex - 1] = newDropdowns[key];
            } else if (oldIndex < index) {
                reindexedSearchTerms[oldIndex] = newSearchTerms[key];
                reindexedDropdowns[oldIndex] = newDropdowns[key];
            }
        });
        setItemSearchTerms(reindexedSearchTerms);
        setItemDropdowns(reindexedDropdowns);
    };

    const addCreditNote = () => {
        setFormData(prev => ({
            ...prev,
            creditNotes: [...(prev.creditNotes || []), { type: 'aftersalesservice', amountSGD: '', remarks: '' }]
        }));
    };

    const removeCreditNote = (index) => {
        setFormData(prev => ({
            ...prev,
            creditNotes: (prev.creditNotes || []).filter((_, i) => i !== index)
        }));
    };

    const updateCreditNote = (index, field, value) => {
        setFormData(prev => {
            const notes = [...(prev.creditNotes || [])];
            if (!notes[index]) return prev;
            notes[index] = { ...notes[index], [field]: value };
            return { ...prev, creditNotes: notes };
        });
    };

    // Calculate TotalSales (sum of selling price of all items - discount + deliveryFees - sum of creditNotes.amountSGD)
    const calculateTotalSales = () => {
        const totalSellingPrice = formData.items.reduce((sum, item) => {
            const price = parseFloat(item.sellingPrice) || 0;
            return sum + price;
        }, 0);
        const discount = parseFloat(formData.discount) || 0;
        const deliveryFees = parseFloat(formData.deliveryFees) || 0;
        const creditNoteTotal = (formData.creditNotes || []).reduce((sum, cn) => sum + (parseFloat(cn.amountSGD) || 0), 0);
        return totalSellingPrice - discount + deliveryFees - creditNoteTotal;
    };

    // Calculate Total Profit (totalSales - (sum of costinsgd of all items) - shipcostsgd)
    const calculateTotalProfit = () => {
        const totalSales = calculateTotalSales();
        const totalCostInSGD = formData.items.reduce((sum, item) => {
            const cost = parseFloat(item.costInSGD) || 0;
            return sum + cost;
        }, 0);
        const shipCostSGD = parseFloat(formData.shipCostSGD) || 0;
        return totalSales - totalCostInSGD - shipCostSGD;
    };

    // Calculate Margin (totalprofit/totalSales)
    const calculateMargin = () => {
        const totalSales = calculateTotalSales();
        const totalProfit = calculateTotalProfit();
        if (totalSales === 0) return 0;
        return (totalProfit / totalSales) * 100; // Return as percentage
    };

    // Formatting helper functions
    const formatRate = (value) => {
        if (!value && value !== 0) return '';
        return parseFloat(value).toFixed(2);
    };

    const formatRMB = (value) => {
        if (!value && value !== 0) return '';
        return `RMB ${parseFloat(value).toFixed(2)}`;
    };

    const formatSGD = (value) => {
        if (!value && value !== 0) return '';
        return `$${parseFloat(value).toFixed(2)}`;
    };

    const formatMargin = (value) => {
        if (!value && value !== 0) return '';
        return `${parseFloat(value).toFixed(2)}%`;
    };

    const updateItem = (index, field, value) => {
        const newItems = [...formData.items];
        newItems[index] = { ...newItems[index], [field]: value };
        
        // Auto-calculate costInSGD when costRMB changes
        if (field === 'costRMB') {
            const costRMB = parseFloat(value) || 0;
            const rate = parseFloat(formData.rate) || 1;
            if (rate > 0 && costRMB > 0) {
                newItems[index].costInSGD = (costRMB / rate).toFixed(2);
            } else {
                newItems[index].costInSGD = '';
            }
            // Recalculate itemProfitSGD
            const sellingPrice = parseFloat(newItems[index].sellingPrice) || 0;
            const costInSGD = parseFloat(newItems[index].costInSGD) || 0;
            newItems[index].itemProfitSGD = (sellingPrice - costInSGD).toFixed(2);
        }
        
        // Auto-calculate itemProfitSGD when sellingPrice changes
        if (field === 'sellingPrice') {
            const sellingPrice = parseFloat(value) || 0;
            const costInSGD = parseFloat(newItems[index].costInSGD) || 0;
            newItems[index].itemProfitSGD = (sellingPrice - costInSGD).toFixed(2);
        }
        
        // If category changes, reset subcategory and get subcategories
        if (field === 'category') {
            newItems[index].subcategory = '';
            const subcategories = getSubcategoriesForCategory(value);
            setCategorySubcategories({
                ...categorySubcategories,
                [index]: subcategories
            });
            // Clear filtered categories since category is now selected
            setFilteredCategories({
                ...filteredCategories,
                [index]: null
            });
        }
        
        // If subcategory changes first, find matching categories
        if (field === 'subcategory') {
            if (!value || !value.trim()) {
                // Subcategory cleared, reset everything
                setFilteredCategories({
                    ...filteredCategories,
                    [index]: null
                });
                setCategorySubcategories({
                    ...categorySubcategories,
                    [index]: []
                });
            } else {
                const matchingCategories = getCategoriesForSubcategory(value);
                
                if (matchingCategories.length === 1) {
                    // Auto-select category if only one category contains this subcategory
                    newItems[index].category = matchingCategories[0]._id;
                    const subcategories = getSubcategoriesForCategory(matchingCategories[0]._id);
                    setCategorySubcategories({
                        ...categorySubcategories,
                        [index]: subcategories
                    });
                    setFilteredCategories({
                        ...filteredCategories,
                        [index]: null
                    });
                } else if (matchingCategories.length > 1) {
                    // If multiple categories contain this subcategory, filter category dropdown
                    const matchingCategoryIds = matchingCategories.map(c => c._id);
                    setFilteredCategories({
                        ...filteredCategories,
                        [index]: matchingCategoryIds
                    });
                    
                    // If current category doesn't contain this subcategory, clear it
                    if (!matchingCategoryIds.includes(newItems[index].category)) {
                        newItems[index].category = '';
                        setCategorySubcategories({
                            ...categorySubcategories,
                            [index]: []
                        });
                    } else {
                        // Current category is valid, update subcategories
                        const subcategories = getSubcategoriesForCategory(newItems[index].category);
                        setCategorySubcategories({
                            ...categorySubcategories,
                            [index]: subcategories
                        });
                    }
                } else {
                    // No matching categories found, clear category
                    newItems[index].category = '';
                    setCategorySubcategories({
                        ...categorySubcategories,
                        [index]: []
                    });
                    setFilteredCategories({
                        ...filteredCategories,
                        [index]: null
                    });
                }
            }
        }
        
        setFormData({ ...formData, items: newItems });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            setError('');

            // Clean and format data
            const cleanedData = {
                purchaseCode: formData.purchaseCode.trim(),
                webOrderID: formData.webOrderID.trim() || undefined,
                trackingNumber: formData.trackingNumber.trim() || undefined,
                customer: formData.customer,
                orderEmail: (formData.orderEmail && formData.orderEmail.trim()) || undefined,
                orderAddress: (formData.orderAddress && formData.orderAddress.trim()) || undefined,
                orderPhone: (formData.orderPhone && formData.orderPhone.trim()) || undefined,
                orderWhatsapp: (formData.orderWhatsapp && formData.orderWhatsapp.trim()) || undefined,
                orderTelegram: (formData.orderTelegram && ((formData.orderTelegram.username && formData.orderTelegram.username.trim()) || (formData.orderTelegram.tgNumber && formData.orderTelegram.tgNumber.trim())))
                    ? { username: (formData.orderTelegram.username && formData.orderTelegram.username.trim()) || undefined, tgNumber: (formData.orderTelegram.tgNumber && formData.orderTelegram.tgNumber.trim()) || undefined }
                    : undefined,
                items: formData.items.map(item => {
                    // Calculate costInSGD if not already set or if costRMB/rate changed
                    const costRMB = parseFloat(item.costRMB) || 0;
                    const rate = parseFloat(formData.rate) || 1;
                    let costInSGD = item.costInSGD;
                    if (rate > 0 && costRMB > 0) {
                        costInSGD = (costRMB / rate).toFixed(2);
                    }
                    
                    // Calculate itemProfitSGD
                    const sellingPrice = parseFloat(item.sellingPrice) || 0;
                    const itemProfitSGD = (sellingPrice - (costInSGD ? parseFloat(costInSGD) : 0)).toFixed(2);
                    
                    return {
                        orderingcode: item.orderingcode.trim() || undefined,
                        category: item.category,
                        subcategory: item.subcategory.trim() || undefined,
                        brand: item.brand || undefined,
                        orderDate: item.orderDate ? new Date(item.orderDate) : undefined,
                        sellingPrice: item.sellingPrice ? parseFloat(item.sellingPrice) : undefined,
                        costRMB: costRMB || undefined,
                        supplier: item.supplier || undefined,
                        doReceiveDate: item.doReceiveDate ? new Date(item.doReceiveDate) : undefined,
                        warehouseRcvDate: item.warehouseRcvDate ? new Date(item.warehouseRcvDate) : undefined,
                        costInSGD: costInSGD ? parseFloat(costInSGD) : undefined,
                        itemProfitSGD: itemProfitSGD ? parseFloat(itemProfitSGD) : undefined,
                        remarks: item.remarks.trim() || undefined
                    };
                }).filter(item => item.category), // Remove items without category
                deliveryFees: formData.deliveryFees ? parseFloat(formData.deliveryFees) : 0,
                discount: formData.discount ? parseFloat(formData.discount) : 0,
                shippedOutDate: formData.shippedOutDate ? new Date(formData.shippedOutDate) : undefined,
                customerReceiveDate: formData.customerReceiveDate ? new Date(formData.customerReceiveDate) : undefined,
                shippingBillDate: formData.shippingBillDate ? new Date(formData.shippingBillDate) : undefined,
                logistic: formData.logistic || undefined,
                rate: formData.rate ? parseFloat(formData.rate) : undefined,
                shipCostRMB: formData.shipCostRMB ? parseFloat(formData.shipCostRMB) : undefined,
                shipCostSGD: (() => {
                    const shipCostRMB = parseFloat(formData.shipCostRMB) || 0;
                    const rate = parseFloat(formData.rate) || 1;
                    if (rate > 0 && shipCostRMB > 0) {
                        return parseFloat((shipCostRMB / rate).toFixed(2));
                    }
                    return undefined;
                })(),
                totalProfit: calculateTotalProfit(),
                margin: calculateMargin(),
                paymentMethod: formData.paymentMethod || 'SGD',
                remarks: formData.remarks.trim() || undefined,
                creditNotes: Array.isArray(formData.creditNotes) ? formData.creditNotes.map(cn => ({
                    type: cn.type,
                    amountSGD: parseFloat(cn.amountSGD) || 0,
                    remarks: (cn.remarks && cn.remarks.trim()) || ''
                })).filter(cn => cn.type) : []
            };

            const token = localStorage.getItem('token');
            const response = await axios.put(
                `${process.env.REACT_APP_API_URL}/api/foorders/${order._id}`,
                cleanedData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );

            if (response.data.success) {
                onSuccess(response.data.data);
            }
        } catch (err) {
            setError(`Failed to update GZ order: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleCustomerEditSuccess = (updatedCustomer) => {
        // Update selected customer details
        setSelectedCustomerDetails(updatedCustomer);
        // Update customer in the list
        const updatedCustomers = customers.map(c => 
            c._id === updatedCustomer._id ? updatedCustomer : c
        );
        setCustomers(updatedCustomers);
        // Update search term
        setCustomerSearchTerm(`${updatedCustomer.customerID} - ${updatedCustomer.Name}`);
        setShowEditCustomerModal(false);
    };

    const handleCustomerCreateSuccess = (newCustomer) => {
        // Add new customer to the list
        setCustomers([...customers, newCustomer]);
        // Select the new customer
        handleCustomerSelect(newCustomer._id);
        setShowCreateCustomerModal(false);
    };

    // Helper function to get filtered brands
    const getFilteredBrands = (index) => {
        const searchTerm = (itemSearchTerms[index]?.brand || '').toLowerCase();
        if (!searchTerm) return brands;
        return brands.filter(brand => 
            brand.Brand && brand.Brand.toLowerCase().includes(searchTerm)
        );
    };

    // Helper function to get filtered categories
    const getFilteredCategories = (index) => {
        const searchTerm = (itemSearchTerms[index]?.category || '').toLowerCase();
        const item = formData.items[index];
        
        // If subcategory is selected, filter categories that contain it
        if (item.subcategory && filteredCategories[index] && Array.isArray(filteredCategories[index])) {
            let categoriesToFilter = categories.filter(cat => 
                filteredCategories[index].includes(cat._id)
            );
            if (searchTerm) {
                categoriesToFilter = categoriesToFilter.filter(cat =>
                    cat.category && cat.category.toLowerCase().includes(searchTerm)
                );
            }
            return categoriesToFilter;
        }
        
        if (!searchTerm) return categories;
        return categories.filter(cat => 
            cat.category && cat.category.toLowerCase().includes(searchTerm)
        );
    };

    // Helper function to get filtered subcategories
    const getFilteredSubcategories = (index) => {
        const searchTerm = (itemSearchTerms[index]?.subcategory || '').toLowerCase();
        const item = formData.items[index];
        
        if (item.category) {
            const subcategories = getSubcategoriesForCategory(item.category);
            if (!searchTerm) return subcategories;
            return subcategories.filter(sub => 
                sub && sub.toLowerCase().includes(searchTerm)
            );
        }
        
        const allSubcategories = getAllSubcategories();
        if (!searchTerm) return allSubcategories;
        return allSubcategories.filter(sub => 
            sub && sub.toLowerCase().includes(searchTerm)
        );
    };

    // Helper function to get filtered suppliers
    const getFilteredSuppliers = (index) => {
        const searchTerm = (itemSearchTerms[index]?.supplier || '').toLowerCase();
        if (!searchTerm) return suppliers;
        return suppliers.filter(supplier => {
            const code = supplier.supplierCode ? supplier.supplierCode.toLowerCase() : '';
            const name = supplier.supplierName ? supplier.supplierName.toLowerCase() : '';
            return code.includes(searchTerm) || name.includes(searchTerm);
        });
    };

    // Function to create new brand
    const createNewBrand = async (index, brandName) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/foproducts/brands`,
                { Brand: brandName.trim() },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            if (response.data.success) {
                const newBrand = response.data.data;
                setBrands(prev => [...prev, newBrand]);
                // Use functional updaters so we don't overwrite with stale state (avoids item going missing)
                setFormData(prev => {
                    const newItems = [...prev.items];
                    if (index < 0 || index >= newItems.length) return prev;
                    newItems[index] = { ...newItems[index], brand: newBrand._id };
                    return { ...prev, items: newItems };
                });
                setItemSearchTerms(prev => ({
                    ...prev,
                    [index]: { ...prev[index], brand: newBrand.Brand }
                }));
                setItemDropdowns(prev => ({
                    ...prev,
                    [index]: { ...prev[index], brand: false }
                }));
            }
        } catch (err) {
            console.error('Failed to create brand:', err);
            setError(`Failed to create brand: ${err.response?.data?.error || err.message}`);
        }
    };

    // Function to create new supplier
    const createNewSupplier = async (index, supplierCode) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/foproducts/suppliers`,
                { supplierCode: supplierCode.trim(), supplierName: '' },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            if (response.data.success) {
                const newSupplier = response.data.data;
                setSuppliers(prev => [...prev, newSupplier]);
                // Use functional updaters so we don't overwrite with stale state (avoids item going missing)
                setFormData(prev => {
                    const newItems = [...prev.items];
                    if (index < 0 || index >= newItems.length) return prev;
                    newItems[index] = { ...newItems[index], supplier: newSupplier._id };
                    return { ...prev, items: newItems };
                });
                setItemSearchTerms(prev => ({
                    ...prev,
                    [index]: { ...prev[index], supplier: newSupplier.supplierCode }
                }));
                setItemDropdowns(prev => ({
                    ...prev,
                    [index]: { ...prev[index], supplier: false }
                }));
            }
        } catch (err) {
            console.error('Failed to create supplier:', err);
            setError(`Failed to create supplier: ${err.response?.data?.error || err.message}`);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-lg p-6 w-full max-w-6xl my-8">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">Edit GZ Order</h2>
                    <button
                        onClick={onCancel}
                        className="text-gray-500 hover:text-gray-700"
                        aria-label="Close modal"
                    >
                        <FiX className="w-6 h-6" />
                    </button>
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
                        {/* First Card - Light Yellow Background */}
                        <div className="p-4 rounded-lg" style={{ backgroundColor: '#FEF9E7' }}>
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-lg font-semibold">Shipping Information</h3>
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={!!formData.shippingBillDate}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                // Set to today's date
                                                const today = new Date();
                                                const year = today.getFullYear();
                                                const month = String(today.getMonth() + 1).padStart(2, '0');
                                                const day = String(today.getDate()).padStart(2, '0');
                                                const todayString = `${year}-${month}-${day}`;
                                                setFormData({ ...formData, shippingBillDate: todayString });
                                            } else {
                                                // Clear the date
                                                setFormData({ ...formData, shippingBillDate: '' });
                                            }
                                        }}
                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    />
                                    <label className="ml-2 text-sm font-medium text-gray-700">
                                        Shipping Bill Date
                                    </label>
                                    {formData.shippingBillDate && (
                                        <span className="ml-3 text-sm text-gray-600">
                                            {new Date(formData.shippingBillDate).toLocaleDateString()}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Logistic
                                    </label>
                                    <select
                                        value={formData.logistic}
                                        onChange={(e) => setFormData({ ...formData, logistic: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    >
                                        <option value="">Select Logistic</option>
                                        <option value="Ken">Ken</option>
                                        <option value="LHZ">LHZ</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Tracking Number
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.trackingNumber}
                                        onChange={(e) => setFormData({ ...formData, trackingNumber: e.target.value })}
                                        onBlur={(e) => {
                                            const v = (e.target.value || '').toUpperCase();
                                            if (v !== e.target.value) setFormData(prev => ({ ...prev, trackingNumber: v }));
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        placeholder="MY8228xxxxxxx"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Ship Cost RMB
                                    </label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">RMB</span>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={formData.shipCostRMB}
                                            onChange={(e) => {
                                                const shipCostRMB = e.target.value;
                                                const rate = parseFloat(formData.rate) || 1;
                                                let shipCostSGD = '';
                                                if (rate > 0 && shipCostRMB) {
                                                    shipCostSGD = (parseFloat(shipCostRMB) / rate).toFixed(2);
                                                }
                                                setFormData({ ...formData, shipCostRMB, shipCostSGD });
                                            }}
                                            className="w-full pl-12 pr-3 py-2 border border-gray-300 rounded-md"
                                            placeholder="0.00"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Shipped Out Date
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.shippedOutDate}
                                        onChange={(e) => setFormData({ ...formData, shippedOutDate: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Customer Receive Date
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.customerReceiveDate}
                                        onChange={(e) => setFormData({ ...formData, customerReceiveDate: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Second Card - Light Green Background */}
                        <div className="p-4 rounded-lg" style={{ backgroundColor: '#D5F4E6' }}>
                            <h3 className="text-lg font-semibold mb-3">Order Information</h3>
                            <div className="space-y-4">
                                {/* Row 1: Purchase Code (20%), Web Order ID (20%), Payment Method (15%), SGD/RMB Rate (15%), Delivery Fees (15%), Discount (15%) */}
                                <div className="flex gap-4">
                                    <div style={{ width: '20%' }}>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Purchase Code *
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.purchaseCode}
                                            onChange={(e) => setFormData({ ...formData, purchaseCode: e.target.value })}
                                            onBlur={(e) => {
                                                const v = (e.target.value || '').toUpperCase();
                                                if (v !== e.target.value) setFormData(prev => ({ ...prev, purchaseCode: v }));
                                            }}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                            placeholder="1505FA07XA"
                                        />
                                    </div>
                                    <div style={{ width: '20%' }}>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Web Order ID
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.webOrderID}
                                            onChange={(e) => setFormData({ ...formData, webOrderID: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                            placeholder="#413555"
                                        />
                                    </div>
                                    <div style={{ width: '15%' }}>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Payment Method
                                        </label>
                                        <select
                                            value={formData.paymentMethod}
                                            onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        >
                                            <option value="SGD">SGD</option>
                                            <option value="Crypto">Crypto</option>
                                        </select>
                                    </div>
                                    <div style={{ width: '15%' }}>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            SGD/RMB Rate
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={formData.rate}
                                            onChange={(e) => {
                                                const newRate = e.target.value;
                                                const rate = parseFloat(newRate) || 1;
                                                // Recalculate all items' costInSGD when rate changes
                                                const updatedItems = formData.items.map(item => {
                                                    const costRMB = parseFloat(item.costRMB) || 0;
                                                    const sellingPrice = parseFloat(item.sellingPrice) || 0;
                                                    if (rate > 0 && costRMB > 0) {
                                                        const costInSGD = (costRMB / rate).toFixed(2);
                                                        const itemProfitSGD = (sellingPrice - parseFloat(costInSGD)).toFixed(2);
                                                        return { ...item, costInSGD, itemProfitSGD };
                                                    }
                                                    return item;
                                                });
                                                // Recalculate shipCostSGD when rate changes
                                                const shipCostRMB = parseFloat(formData.shipCostRMB) || 0;
                                                let shipCostSGD = '';
                                                if (rate > 0 && shipCostRMB > 0) {
                                                    shipCostSGD = (shipCostRMB / rate).toFixed(2);
                                                }
                                                setFormData({ ...formData, rate: newRate, items: updatedItems, shipCostSGD });
                                            }}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                            placeholder="0.00"
                                        />
                                    </div>
                                    <div style={{ width: '15%' }}>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Delivery Fees
                                        </label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">$</span>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={formData.deliveryFees}
                                                onChange={(e) => setFormData({ ...formData, deliveryFees: e.target.value })}
                                                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md"
                                                placeholder="0.00"
                                            />
                                        </div>
                                    </div>
                                    <div style={{ width: '15%' }}>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Discount
                                        </label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">$</span>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={formData.discount}
                                                onChange={(e) => setFormData({ ...formData, discount: e.target.value })}
                                                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md"
                                                placeholder="0.00"
                                            />
                                        </div>
                                    </div>
                                </div>
                                {/* Row 2: Customer */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Customer *
                                    </label>
                                    <div className="flex gap-2">
                                        <div className="relative flex-1">
                                            <input
                                                type="text"
                                                required
                                                value={customerSearchTerm}
                                                onChange={(e) => {
                                                    setCustomerSearchTerm(e.target.value);
                                                    setShowCustomerDropdown(true);
                                                    if (!e.target.value) {
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            customer: '',
                                                            orderEmail: '',
                                                            orderAddress: '',
                                                            orderPhone: '',
                                                            orderWhatsapp: '',
                                                            orderTelegram: { username: '', tgNumber: '' }
                                                        }));
                                                        setSelectedCustomerDetails(null);
                                                    }
                                                }}
                                                onFocus={() => setShowCustomerDropdown(true)}
                                                placeholder="Type to search by Customer ID or Name..."
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                            />
                                            {showCustomerDropdown && (
                                                <div 
                                                    className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {filteredCustomers.length === 0 ? (
                                                        <div className="px-3 py-2 text-sm text-gray-500">
                                                            No customers found
                                                        </div>
                                                    ) : (
                                                        filteredCustomers.map(customer => (
                                                            <div
                                                                key={customer._id}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleCustomerSelect(customer._id);
                                                                }}
                                                                className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                                                            >
                                                                <div className="font-medium text-gray-900">
                                                                    {customer.customerID}
                                                                </div>
                                                                <div className="text-gray-600 text-xs">
                                                                    {customer.Name}
                                                                </div>
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setShowCreateCustomerModal(true)}
                                            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 whitespace-nowrap"
                                        >
                                            Create New Customer
                                        </button>
                                    </div>
                                    
                                    {/* Customer Details Display + Contact for this order */}
                                    {selectedCustomerDetails && (
                                        <div className="mt-3 p-4 bg-white border border-gray-200 rounded-lg relative">
                                            <button
                                                type="button"
                                                onClick={() => setShowEditCustomerModal(true)}
                                                className="absolute top-2 right-2 p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded-full"
                                                title="Edit Customer"
                                            >
                                                <FiEdit2 className="w-4 h-4" />
                                            </button>
                                            <div className="pr-8">
                                                <div className="font-semibold text-gray-900 mb-3">
                                                    {selectedCustomerDetails.customerID} - {selectedCustomerDetails.Name}
                                                </div>
                                                <div className="text-sm font-medium text-gray-700 mb-2">Contact for this order (choose one per type)</div>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 text-sm">
                                                    <div>
                                                        <label className="block text-gray-600 mb-0.5">Email ({(selectedCustomerDetails.email || []).length})</label>
                                                        <select
                                                            value={formData.orderEmail}
                                                            onChange={(e) => setFormData(prev => ({ ...prev, orderEmail: e.target.value }))}
                                                            className="w-full px-2 py-1.5 border border-gray-300 rounded-md bg-white"
                                                        >
                                                            <option value="">—</option>
                                                            {(selectedCustomerDetails.email || []).map((em, i) => (
                                                                <option key={i} value={em}>{em}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div className="min-w-0">
                                                        <label className="block text-gray-600 mb-0.5">Address ({(selectedCustomerDetails.Address || []).length})</label>
                                                        <select
                                                            value={formData.orderAddress}
                                                            onChange={(e) => setFormData(prev => ({ ...prev, orderAddress: e.target.value }))}
                                                            className="w-full px-2 py-1.5 border border-gray-300 rounded-md bg-white"
                                                        >
                                                            <option value="">—</option>
                                                            {(selectedCustomerDetails.Address || []).map((addr, i) => (
                                                                <option key={i} value={addr}>{addr}</option>
                                                            ))}
                                                        </select>
                                                        {formData.orderAddress && (
                                                            <div className="mt-1 text-xs text-gray-600 min-w-0 break-words whitespace-normal">
                                                                {formData.orderAddress}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <label className="block text-gray-600 mb-0.5">Phone ({(selectedCustomerDetails.Phone || []).length})</label>
                                                        <select
                                                            value={formData.orderPhone}
                                                            onChange={(e) => setFormData(prev => ({ ...prev, orderPhone: e.target.value }))}
                                                            className="w-full px-2 py-1.5 border border-gray-300 rounded-md bg-white"
                                                        >
                                                            <option value="">—</option>
                                                            {(selectedCustomerDetails.Phone || []).map((ph, i) => (
                                                                <option key={i} value={ph}>{ph}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-gray-600 mb-0.5">WhatsApp ({(selectedCustomerDetails.Whatsapp || []).length})</label>
                                                        <select
                                                            value={formData.orderWhatsapp}
                                                            onChange={(e) => setFormData(prev => ({ ...prev, orderWhatsapp: e.target.value }))}
                                                            className="w-full px-2 py-1.5 border border-gray-300 rounded-md bg-white"
                                                        >
                                                            <option value="">—</option>
                                                            {(selectedCustomerDetails.Whatsapp || []).map((wa, i) => (
                                                                <option key={i} value={wa}>{wa}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-gray-600 mb-0.5">Telegram ({(selectedCustomerDetails.Telegram || []).length})</label>
                                                        <select
                                                            value={(() => {
                                                                const arr = selectedCustomerDetails.Telegram || [];
                                                                const idx = arr.findIndex(t => {
                                                                    const u = typeof t === 'string' ? t : (t?.username || '');
                                                                    const n = typeof t === 'string' ? '' : (t?.tgNumber || '');
                                                                    return u === (formData.orderTelegram?.username || '') && n === (formData.orderTelegram?.tgNumber || '');
                                                                });
                                                                return idx >= 0 ? String(idx) : '';
                                                            })()}
                                                            onChange={(e) => {
                                                                const i = e.target.value === '' ? -1 : parseInt(e.target.value, 10);
                                                                const arr = selectedCustomerDetails.Telegram || [];
                                                                const t = i >= 0 && arr[i] ? arr[i] : null;
                                                                const next = t == null ? { username: '', tgNumber: '' } : {
                                                                    username: typeof t === 'string' ? t : (t?.username || ''),
                                                                    tgNumber: typeof t === 'string' ? '' : (t?.tgNumber || '')
                                                                };
                                                                setFormData(prev => ({ ...prev, orderTelegram: next }));
                                                            }}
                                                            className="w-full px-2 py-1.5 border border-gray-300 rounded-md bg-white"
                                                        >
                                                            <option value="">—</option>
                                                            {(selectedCustomerDetails.Telegram || []).map((t, i) => {
                                                                const label = typeof t === 'string' ? t : [t?.username, t?.tgNumber].filter(Boolean).join(' – ') || '—';
                                                                return <option key={i} value={i}>{label}</option>;
                                                            })}
                                                        </select>
                                                    </div>
                                                </div>
                                                {/* Measurements / Remarks (small font) - only if at least one value present */}
                                                {(() => {
                                                    const c = selectedCustomerDetails;
                                                    const hasHeight = c.Height != null && c.Height !== '';
                                                    const hasWeight = c.Weight != null && c.Weight !== '';
                                                    const hasWaist = c.waist != null && c.waist !== '';
                                                    const hasWrist = c.wrist != null && c.wrist !== '';
                                                    const hasShoeSize = c.shoeSize != null && c.shoeSize !== '';
                                                    const hasRingSize = c.ringSize != null && c.ringSize !== '';
                                                    const hasRemarks = c.remarks && String(c.remarks).trim() !== '';
                                                    const parts = [];
                                                    if (hasHeight) parts.push(`Height: ${Number(c.Height).toFixed(0)} cm`);
                                                    if (hasWeight) parts.push(`Weight: ${Number(c.Weight).toFixed(2)} kg`);
                                                    if (hasWaist) parts.push(`Waist: ${Number(c.waist).toFixed(2)} cm`);
                                                    if (hasWrist) parts.push(`Wrist: ${Number(c.wrist).toFixed(2)} cm`);
                                                    if (hasShoeSize) parts.push(`Shoe size: ${Number(c.shoeSize).toFixed(2)}`);
                                                    if (hasRingSize) parts.push(`Ring size: ${Number(c.ringSize).toFixed(2)}`);
                                                    const hasAnyMeasurement = parts.length > 0;
                                                    if (!hasAnyMeasurement && !hasRemarks) return null;
                                                    return (
                                                        <div className="mt-3 text-xs text-gray-600 border-t border-gray-100 pt-2">
                                                            {hasAnyMeasurement && (
                                                                <div className="mb-1">{parts.join(', ')}</div>
                                                            )}
                                                            {hasRemarks && (
                                                                <div><span className="font-medium">Remarks:</span> {c.remarks}</div>
                                                            )}
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Third Card - Light Orange Background */}
                        <div className="p-4 rounded-lg" style={{ backgroundColor: '#FFE5B4' }}>
                            <h3 className="text-lg font-semibold mb-3">Financial Summary</h3>
                            <div className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Total Sales (auto calculated)
                                        </label>
                                        <input
                                            type="text"
                                            value={formatSGD(calculateTotalSales())}
                                            disabled
                                            style={{ backgroundColor: '#D6CFC7' }}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md font-semibold text-blue-600"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Ship Cost (SGD - auto calculated)
                                        </label>
                                        <input
                                            type="text"
                                            value={(() => {
                                                const shipCostRMB = parseFloat(formData.shipCostRMB) || 0;
                                                const rate = parseFloat(formData.rate) || 1;
                                                if (rate > 0 && shipCostRMB > 0) {
                                                    return formatSGD(shipCostRMB / rate);
                                                }
                                                return '';
                                            })()}
                                            disabled
                                            style={{ backgroundColor: '#D6CFC7' }}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md font-semibold text-blue-600"
                                            placeholder="0.00"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Total Profit (auto calculated after shipCostRMB updated)
                                        </label>
                                        <input
                                            type="text"
                                            value={formatSGD(calculateTotalProfit())}
                                            disabled
                                            style={{ backgroundColor: '#D6CFC7' }}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md font-semibold text-blue-600"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Margin
                                        </label>
                                        <input
                                            type="text"
                                            value={formatMargin(calculateMargin())}
                                            disabled
                                            style={{ backgroundColor: '#D6CFC7' }}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md font-semibold text-blue-600"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Credit Notes Card - below Financial Summary */}
                        {(formData.creditNotes && formData.creditNotes.length > 0) && (
                            <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                                <h3 className="text-lg font-semibold mb-3">Credit Notes ({formData.creditNotes.length})</h3>
                                <div className="space-y-4">
                                    {(formData.creditNotes || []).map((cn, index) => (
                                        <div key={index} className="p-4 bg-white border border-amber-200 rounded-lg">
                                            <div className="flex justify-between items-center mb-3">
                                                <span className="text-sm font-medium text-gray-700">Credit Note {index + 1}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeCreditNote(index)}
                                                    className="text-red-600 hover:text-red-800"
                                                >
                                                    <FiTrash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                                                    <select
                                                        value={cn.type || 'refund'}
                                                        onChange={(e) => updateCreditNote(index, 'type', e.target.value)}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                                    >
                                                        <option value="refund">Refund</option>
                                                        <option value="aftersalesservice">After-sales service</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount (SGD)</label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        value={cn.amountSGD === '' ? '' : cn.amountSGD}
                                                        onChange={(e) => updateCreditNote(index, 'amountSGD', e.target.value)}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                                        placeholder="0.00"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                                                    <input
                                                        type="text"
                                                        value={cn.remarks || ''}
                                                        onChange={(e) => updateCreditNote(index, 'remarks', e.target.value)}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                                        placeholder="Optional"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Fourth Card - Light Blue Background */}
                        <div className="p-4 rounded-lg" style={{ backgroundColor: '#E6F3FF' }}>
                            <div className="flex justify-between items-center mb-3">
                                <h3 className="text-lg font-semibold">Order Items ({formData.items.length})</h3>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={addItem}
                                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                                    >
                                        <FiPlus className="mr-1" />
                                        Add Item
                                    </button>
                                    <button
                                        type="button"
                                        onClick={addCreditNote}
                                        className="px-3 py-1 text-sm bg-amber-600 text-white rounded-md hover:bg-amber-700 flex items-center"
                                    >
                                        <FiPlus className="mr-1" />
                                        Add Credit Note
                                    </button>
                                </div>
                            </div>
                            {formData.items.map((item, index) => (
                                    <div key={index} className="mb-4 p-4 bg-white border border-gray-200 rounded-lg">
                                        <div className="flex justify-between items-center mb-3">
                                            <span className="text-sm font-medium text-gray-700">Item {index + 1}</span>
                                            {formData.items.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeItem(index)}
                                                    className="text-red-600 hover:text-red-800"
                                                >
                                                    <FiTrash2 className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                        {/* Row 1: ordering code, brand, subcategory, category */}
                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Ordering Code
                                                </label>
                                                <input
                                                    type="text"
                                                    value={item.orderingcode}
                                                    onChange={(e) => updateItem(index, 'orderingcode', e.target.value)}
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
                                                    placeholder="BMCG126710"
                                                />
                                            </div>
                                            <div className="relative">
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Brand
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        type="text"
                                                        value={(itemSearchTerms[index] && itemSearchTerms[index].brand) || (item.brand ? (brands.find(b => b._id === item.brand) && brands.find(b => b._id === item.brand).Brand) || '' : '')}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            setItemSearchTerms({
                                                                ...itemSearchTerms,
                                                                [index]: { ...itemSearchTerms[index], brand: value }
                                                            });
                                                            setItemDropdowns({
                                                                ...itemDropdowns,
                                                                [index]: { ...itemDropdowns[index], brand: true }
                                                            });
                                                            // Clear selection if input changes
                                                            if (!value) {
                                                                updateItem(index, 'brand', '');
                                                            }
                                                        }}
                                                        onFocus={() => setItemDropdowns({
                                                            ...itemDropdowns,
                                                            [index]: { ...itemDropdowns[index], brand: true }
                                                        })}
                                                        className="w-full px-2 py-1 pr-8 text-sm border border-gray-300 rounded-md"
                                                        placeholder="Type to search brand (LV)"
                                                    />
                                                    {item.brand && (
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                updateItem(index, 'brand', '');
                                                                setItemSearchTerms({
                                                                    ...itemSearchTerms,
                                                                    [index]: { ...itemSearchTerms[index], brand: '' }
                                                                });
                                                            }}
                                                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                                        >
                                                            <FiX className="w-3 h-3" />
                                                        </button>
                                                    )}
                                                </div>
                                                {(itemDropdowns[index] && itemDropdowns[index].brand) && (
                                                    <div 
                                                        className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-y-auto"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        {getFilteredBrands(index).length === 0 ? (
                                                            <div className="px-3 py-2 text-sm text-gray-500 flex items-center justify-between">
                                                                <span>No brand found</span>
                                                                {(itemSearchTerms[index] && itemSearchTerms[index].brand) && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            createNewBrand(index, itemSearchTerms[index].brand);
                                                                        }}
                                                                        className="text-blue-600 hover:text-blue-800 flex items-center"
                                                                        title="Add new brand"
                                                                    >
                                                                        <FiPlus className="w-4 h-4 mr-1" />
                                                                        Add "{itemSearchTerms[index].brand}"
                                                                    </button>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            getFilteredBrands(index).map(brand => (
                                                                <div
                                                                    key={brand._id}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        updateItem(index, 'brand', brand._id);
                                                                        setItemSearchTerms({
                                                                            ...itemSearchTerms,
                                                                            [index]: { ...itemSearchTerms[index], brand: brand.Brand }
                                                                        });
                                                                        setItemDropdowns({
                                                                            ...itemDropdowns,
                                                                            [index]: { ...itemDropdowns[index], brand: false }
                                                                        });
                                                                    }}
                                                                    className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                                                                >
                                                                    {brand.Brand}
                                                                </div>
                                                            ))
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="relative">
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Subcategory
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        type="text"
                                                        value={(itemSearchTerms[index] && itemSearchTerms[index].subcategory) || item.subcategory || ''}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            setItemSearchTerms({
                                                                ...itemSearchTerms,
                                                                [index]: { ...itemSearchTerms[index], subcategory: value }
                                                            });
                                                            setItemDropdowns({
                                                                ...itemDropdowns,
                                                                [index]: { ...itemDropdowns[index], subcategory: true }
                                                            });
                                                            updateItem(index, 'subcategory', value);
                                                        }}
                                                        onFocus={() => setItemDropdowns({
                                                            ...itemDropdowns,
                                                            [index]: { ...itemDropdowns[index], subcategory: true }
                                                        })}
                                                        className="w-full px-2 py-1 pr-8 text-sm border border-gray-300 rounded-md"
                                                        placeholder="Type to search (T-Shirts)"
                                                    />
                                                    {item.subcategory && (
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                updateItem(index, 'subcategory', '');
                                                                setItemSearchTerms({
                                                                    ...itemSearchTerms,
                                                                    [index]: { ...itemSearchTerms[index], subcategory: '' }
                                                                });
                                                            }}
                                                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                                        >
                                                            <FiX className="w-3 h-3" />
                                                        </button>
                                                    )}
                                                </div>
                                                {(itemDropdowns[index] && itemDropdowns[index].subcategory) && (
                                                    <div 
                                                        className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-y-auto"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        {getFilteredSubcategories(index).length === 0 ? (
                                                            <div className="px-3 py-2 text-sm text-gray-500 flex items-center justify-between">
                                                                <span>No subcategory found</span>
                                                                {(itemSearchTerms[index] && itemSearchTerms[index].subcategory) && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            const subVal = (itemSearchTerms[index].subcategory || formData.items[index].subcategory || '').trim();
                                                                            const item = formData.items[index];
                                                                            if (item.category) {
                                                                                const categoryDoc = categories.find(c => c._id === item.category);
                                                                                if (categoryDoc) {
                                                                                    setEditCategoryInitial(categoryDoc);
                                                                                    setEditCategoryPrefillCategory('');
                                                                                    setEditCategoryPrefillSub(subVal);
                                                                                } else {
                                                                                    setEditCategoryInitial(null);
                                                                                    setEditCategoryPrefillCategory('');
                                                                                    setEditCategoryPrefillSub(subVal);
                                                                                }
                                                                            } else {
                                                                                setEditCategoryInitial(null);
                                                                                setEditCategoryPrefillCategory('');
                                                                                setEditCategoryPrefillSub(subVal);
                                                                            }
                                                                            setEditCategoryItemIndex(index);
                                                                            setShowEditCategoryForSubcategory(true);
                                                                            setItemDropdowns({ ...itemDropdowns, [index]: { ...itemDropdowns[index], subcategory: false } });
                                                                        }}
                                                                        className="text-blue-600 hover:text-blue-800 flex items-center"
                                                                        title="Add new subcategory"
                                                                    >
                                                                        <FiPlus className="w-4 h-4 mr-1" />
                                                                        Add "{itemSearchTerms[index].subcategory}"
                                                                    </button>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            getFilteredSubcategories(index).map((subcat, idx) => (
                                                                <div
                                                                    key={idx}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        updateItem(index, 'subcategory', subcat);
                                                                        setItemSearchTerms({
                                                                            ...itemSearchTerms,
                                                                            [index]: { ...itemSearchTerms[index], subcategory: subcat }
                                                                        });
                                                                        setItemDropdowns({
                                                                            ...itemDropdowns,
                                                                            [index]: { ...itemDropdowns[index], subcategory: false }
                                                                        });
                                                                    }}
                                                                    className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                                                                >
                                                                    {subcat}
                                                                </div>
                                                            ))
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="relative">
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Category *
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        type="text"
                                                        required
                                                        value={(itemSearchTerms[index] && itemSearchTerms[index].category) || (item.category ? (categories.find(c => c._id === item.category) && categories.find(c => c._id === item.category).category) || '' : '')}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            setItemSearchTerms({
                                                                ...itemSearchTerms,
                                                                [index]: { ...itemSearchTerms[index], category: value }
                                                            });
                                                            setItemDropdowns({
                                                                ...itemDropdowns,
                                                                [index]: { ...itemDropdowns[index], category: true }
                                                            });
                                                            // Clear selection if input changes
                                                            if (!value) {
                                                                updateItem(index, 'category', '');
                                                            }
                                                        }}
                                                        onFocus={() => setItemDropdowns({
                                                            ...itemDropdowns,
                                                            [index]: { ...itemDropdowns[index], category: true }
                                                        })}
                                                        className="w-full px-2 py-1 pr-8 text-sm border border-gray-300 rounded-md"
                                                        placeholder="Type to search (Clothing)"
                                                    />
                                                    {item.category && (
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                updateItem(index, 'category', '');
                                                                setItemSearchTerms({
                                                                    ...itemSearchTerms,
                                                                    [index]: { ...itemSearchTerms[index], category: '' }
                                                                });
                                                            }}
                                                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                                        >
                                                            <FiX className="w-3 h-3" />
                                                        </button>
                                                    )}
                                                </div>
                                                {(itemDropdowns[index] && itemDropdowns[index].category) && (
                                                    <div 
                                                        className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-y-auto"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        {getFilteredCategories(index).length === 0 ? (
                                                            <div className="px-3 py-2 text-sm text-gray-500 flex items-center justify-between">
                                                                <span>No category found</span>
                                                                {(itemSearchTerms[index] && itemSearchTerms[index].category) && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setEditCategoryInitial(null);
                                                                            setEditCategoryPrefillCategory(itemSearchTerms[index].category || '');
                                                                            setEditCategoryPrefillSub('');
                                                                            setEditCategoryItemIndex(index);
                                                                            setShowEditCategoryForSubcategory(true);
                                                                            setItemDropdowns({ ...itemDropdowns, [index]: { ...itemDropdowns[index], category: false } });
                                                                        }}
                                                                        className="text-blue-600 hover:text-blue-800 flex items-center"
                                                                        title="Add new category"
                                                                    >
                                                                        <FiPlus className="w-4 h-4 mr-1" />
                                                                        Add "{itemSearchTerms[index].category}"
                                                                    </button>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            getFilteredCategories(index).map(cat => (
                                                                <div
                                                                    key={cat._id}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        updateItem(index, 'category', cat._id);
                                                                        setItemSearchTerms({
                                                                            ...itemSearchTerms,
                                                                            [index]: { ...itemSearchTerms[index], category: cat.category }
                                                                        });
                                                                        setItemDropdowns({
                                                                            ...itemDropdowns,
                                                                            [index]: { ...itemDropdowns[index], category: false }
                                                                        });
                                                                    }}
                                                                    className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                                                                >
                                                                    {cat.category}
                                                                </div>
                                                            ))
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        {/* Row 2: order date, supplier, do receive date, warehouse receive date */}
                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Order Date
                                                </label>
                                                <input
                                                    type="date"
                                                    value={item.orderDate}
                                                    onChange={(e) => updateItem(index, 'orderDate', e.target.value)}
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
                                                />
                                            </div>
                                            <div className="relative">
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Supplier
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        type="text"
                                                        value={(itemSearchTerms[index] && itemSearchTerms[index].supplier) || (item.supplier ? (suppliers.find(s => s._id === item.supplier) && suppliers.find(s => s._id === item.supplier).supplierCode) || '' : '')}
                                                        onChange={(e) => {
                                                            const value = (e.target.value || '').toUpperCase();
                                                            setItemSearchTerms({
                                                                ...itemSearchTerms,
                                                                [index]: { ...itemSearchTerms[index], supplier: value }
                                                            });
                                                            setItemDropdowns({
                                                                ...itemDropdowns,
                                                                [index]: { ...itemDropdowns[index], supplier: true }
                                                            });
                                                            // Clear selection if input changes
                                                            if (!value) {
                                                                updateItem(index, 'supplier', '');
                                                            }
                                                        }}
                                                        onFocus={() => setItemDropdowns({
                                                            ...itemDropdowns,
                                                            [index]: { ...itemDropdowns[index], supplier: true }
                                                        })}
                                                        onBlur={() => {
                                                            const filtered = getFilteredSuppliers(index);
                                                            const currentSupplierId = formData.items[index]?.supplier;
                                                            if (filtered.length === 1 && currentSupplierId !== filtered[0]._id) {
                                                                const supplier = filtered[0];
                                                                updateItem(index, 'supplier', supplier._id);
                                                                setItemSearchTerms(prev => ({
                                                                    ...prev,
                                                                    [index]: { ...prev[index], supplier: supplier.supplierCode }
                                                                }));
                                                                setItemDropdowns(prev => ({
                                                                    ...prev,
                                                                    [index]: { ...prev[index], supplier: false }
                                                                }));
                                                            }
                                                        }}
                                                        className="w-full px-2 py-1 pr-8 text-sm border border-gray-300 rounded-md"
                                                        placeholder="Type to search supplier..."
                                                    />
                                                    {item.supplier && (
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                updateItem(index, 'supplier', '');
                                                                setItemSearchTerms({
                                                                    ...itemSearchTerms,
                                                                    [index]: { ...itemSearchTerms[index], supplier: '' }
                                                                });
                                                            }}
                                                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                                        >
                                                            <FiX className="w-3 h-3" />
                                                        </button>
                                                    )}
                                                </div>
                                                {(itemDropdowns[index] && itemDropdowns[index].supplier) && (
                                                    <div 
                                                        className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-y-auto"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        {getFilteredSuppliers(index).length === 0 ? (
                                                            <div className="px-3 py-2 text-sm text-gray-500 flex items-center justify-between">
                                                                <span>No supplier found</span>
                                                                {(itemSearchTerms[index] && itemSearchTerms[index].supplier) && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            createNewSupplier(index, itemSearchTerms[index].supplier);
                                                                        }}
                                                                        className="text-blue-600 hover:text-blue-800 flex items-center"
                                                                        title="Add new supplier"
                                                                    >
                                                                        <FiPlus className="w-4 h-4 mr-1" />
                                                                        Add "{itemSearchTerms[index].supplier}"
                                                                    </button>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            getFilteredSuppliers(index).map(supplier => (
                                                                <div
                                                                    key={supplier._id}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        updateItem(index, 'supplier', supplier._id);
                                                                        setItemSearchTerms({
                                                                            ...itemSearchTerms,
                                                                            [index]: { ...itemSearchTerms[index], supplier: supplier.supplierCode }
                                                                        });
                                                                        setItemDropdowns({
                                                                            ...itemDropdowns,
                                                                            [index]: { ...itemDropdowns[index], supplier: false }
                                                                        });
                                                                    }}
                                                                    className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                                                                >
                                                                    {supplier.supplierCode}{supplier.supplierName ? ` - ${supplier.supplierName}` : ''}
                                                                </div>
                                                            ))
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    DO Receive Date
                                                </label>
                                                <input
                                                    type="date"
                                                    value={item.doReceiveDate}
                                                    onChange={(e) => updateItem(index, 'doReceiveDate', e.target.value)}
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Warehouse Receive Date
                                                </label>
                                                <input
                                                    type="date"
                                                    value={item.warehouseRcvDate}
                                                    onChange={(e) => updateItem(index, 'warehouseRcvDate', e.target.value)}
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
                                                />
                                            </div>
                                        </div>
                                        {/* Row 3: selling price, cost rmb, cost in sgd, item profit sgd */}
                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Selling Price
                                                </label>
                                                <div className="relative">
                                                    <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs">$</span>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        value={item.sellingPrice}
                                                        onChange={(e) => updateItem(index, 'sellingPrice', e.target.value)}
                                                        className="w-full pl-7 pr-2 py-1 text-sm border border-gray-300 rounded-md"
                                                        placeholder="0.00"
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Cost RMB
                                                </label>
                                                <div className="relative">
                                                    <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs">RMB</span>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        value={item.costRMB}
                                                        onChange={(e) => updateItem(index, 'costRMB', e.target.value)}
                                                        className="w-full pl-12 pr-2 py-1 text-sm border border-gray-300 rounded-md"
                                                        placeholder="0.00"
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Cost in SGD (auto calculate)
                                                </label>
                                                <input
                                                    type="text"
                                                    value={(() => {
                                                        const costRMB = parseFloat(item.costRMB) || 0;
                                                        const rate = parseFloat(formData.rate) || 1;
                                                        if (rate > 0 && costRMB > 0) {
                                                            return formatSGD(costRMB / rate);
                                                        }
                                                        return '';
                                                    })()}
                                                    disabled
                                                    style={{ backgroundColor: '#D6CFC7' }}
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md font-semibold text-blue-600"
                                                    placeholder="0.00"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Item Profit SGD (auto calculate)
                                                </label>
                                                <input
                                                    type="text"
                                                    value={(() => {
                                                        const sellingPrice = parseFloat(item.sellingPrice) || 0;
                                                        const costRMB = parseFloat(item.costRMB) || 0;
                                                        const rate = parseFloat(formData.rate) || 1;
                                                        if (rate > 0 && costRMB > 0) {
                                                            const costInSGD = costRMB / rate;
                                                            return formatSGD(sellingPrice - costInSGD);
                                                        }
                                                        return '';
                                                    })()}
                                                    disabled
                                                    style={{ backgroundColor: '#D6CFC7' }}
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md font-semibold text-blue-600"
                                                    placeholder="0.00"
                                                />
                                            </div>
                                        </div>
                                        {/* Row 4: Remarks */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Remarks
                                            </label>
                                            <input
                                                type="text"
                                                value={item.remarks}
                                                onChange={(e) => updateItem(index, 'remarks', e.target.value)}
                                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
                                                placeholder="Item remarks"
                                            />
                                        </div>
                                    </div>
                            ))}
                        </div>

                        {/* Order Remarks Card */}
                        <div className="p-4 rounded-lg" style={{ backgroundColor: '#F5F5F5' }}>
                            <h3 className="text-lg font-semibold mb-3">Order Remarks</h3>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Remarks (Long text with line breaks)
                                </label>
                                <textarea
                                    value={formData.remarks}
                                    onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    rows="4"
                                    placeholder="Enter order remarks (supports line breaks)..."
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2 mt-6">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            disabled={loading}
                        >
                            {loading ? 'Updating...' : 'Update'}
                        </button>
                    </div>
                </form>
            </div>
            {/* Backdrop to close dropdowns */}
            {(showCustomerDropdown || Object.keys(itemDropdowns).some(idx => {
                const dropdowns = itemDropdowns[idx];
                return dropdowns && (dropdowns.brand || dropdowns.category || dropdowns.subcategory || dropdowns.supplier);
            })) && (
                <div 
                    className="fixed inset-0" 
                    onClick={() => {
                        setShowCustomerDropdown(false);
                        setItemDropdowns({});
                    }}
                    style={{ zIndex: 40 }}
                />
            )}

            {/* Edit Customer Modal */}
            {showEditCustomerModal && selectedCustomerDetails && (
                <EditFOCustomerModal
                    customer={selectedCustomerDetails}
                    onSuccess={handleCustomerEditSuccess}
                    onCancel={() => setShowEditCustomerModal(false)}
                />
            )}

            {/* Create Customer Modal */}
            {showCreateCustomerModal && (
                <CreateFOCustomerForm
                    onSuccess={handleCustomerCreateSuccess}
                    onCancel={() => setShowCreateCustomerModal(false)}
                />
            )}

            {/* Edit Category Modal (Add category / Add subcategory) */}
            {showEditCategoryForSubcategory && editCategoryItemIndex != null && (
                <EditCategoryModal
                    mode={editCategoryInitial == null ? 'create' : 'edit'}
                    initialCategory={editCategoryInitial || undefined}
                    prefillCategoryInput={editCategoryPrefillCategory || ''}
                    prefillSubcategoryInput={editCategoryPrefillSub}
                    onSuccess={async (categoryId, subcategoryValue, categoryName, updatedSubcategoryList) => {
                        await fetchCategories();
                        const idx = editCategoryItemIndex;
                        if (idx != null) {
                            // Batch update item in one setFormData so we don't trigger updateItem's
                            // category→clear subcategory or subcategory→matching (which can clear category).
                            // Otherwise the item can lose category and go missing.
                            setFormData(prev => {
                                const newItems = [...prev.items];
                                if (idx < 0 || idx >= newItems.length) return prev;
                                const item = newItems[idx];
                                if (categoryId != null && categoryId !== '') {
                                    newItems[idx] = { ...item, category: categoryId };
                                }
                                if (subcategoryValue != null && subcategoryValue !== '') {
                                    newItems[idx] = { ...newItems[idx], subcategory: subcategoryValue.trim() };
                                }
                                return { ...prev, items: newItems };
                            });
                            setItemSearchTerms(prev => ({
                                ...prev,
                                [idx]: {
                                    ...prev[idx],
                                    ...(categoryName != null && categoryName !== '' && { category: categoryName }),
                                    ...(subcategoryValue != null && subcategoryValue !== '' && { subcategory: subcategoryValue.trim() })
                                }
                            }));
                            if (Array.isArray(updatedSubcategoryList)) {
                                setCategorySubcategories(prev => ({ ...prev, [idx]: updatedSubcategoryList }));
                            } else if (categoryId || (editCategoryInitial && editCategoryInitial._id)) {
                                const newCategoryId = categoryId || editCategoryInitial._id;
                                const subcats = getSubcategoriesForCategory(newCategoryId);
                                setCategorySubcategories(prev => ({ ...prev, [idx]: subcats }));
                            }
                        }
                        setShowEditCategoryForSubcategory(false);
                        setEditCategoryItemIndex(null);
                        setEditCategoryInitial(null);
                        setEditCategoryPrefillCategory('');
                        setEditCategoryPrefillSub('');
                    }}
                    onCancel={() => {
                        setShowEditCategoryForSubcategory(false);
                        setEditCategoryItemIndex(null);
                        setEditCategoryInitial(null);
                        setEditCategoryPrefillCategory('');
                        setEditCategoryPrefillSub('');
                    }}
                />
            )}
        </div>
    );
};

export default EditFOOrderModal;
